import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  User,
  MapPin,
  Mail,
  FileText,
  Shield,
  Save,
  RefreshCw,
  Info,
  BarChart3,
  Eye,
  AlertTriangle,
  ScrollText,
  Lock,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';
import { RichTextEditor } from '@/components/ui/RichTextEditor';

const supabase = getSupabaseAdminClient();

// Default disclaimer content
const DEFAULT_DISCLAIMER = `<p>Die in dieser App bereitgestellten Informationen dienen ausschließlich allgemeinen Informations- und Orientierungszwecken. Sie ersetzen in keinem Fall die professionelle Beratung, Diagnose oder Behandlung durch qualifizierte Ärztinnen und Ärzte, Hebammen oder anderes medizinisches Fachpersonal.</p><p>Trotz sorgfältiger inhaltlicher Kontrolle übernehmen wir keine Haftung für die Aktualität, Richtigkeit, Vollständigkeit oder Qualität der bereitgestellten Informationen. Die Inhalte können trotz gewissenhafter Prüfung Fehler enthalten oder veraltet sein.</p><p>Jede Schwangerschaft ist individuell. Entscheidungen, die Ihre Gesundheit oder die Gesundheit Ihres Kindes betreffen, sollten stets in Absprache mit medizinischem Fachpersonal getroffen werden. Bei Beschwerden, Unsicherheiten oder Notfällen wenden Sie sich bitte umgehend an Ihre Ärztin, Ihren Arzt, Ihre Hebamme oder den ärztlichen Notdienst.</p><p>Funktionen wie der Produktscanner, der Behördenguide oder andere Hilfsmittel dieser App basieren auf allgemein verfügbaren Daten und Algorithmen. Die Ergebnisse stellen keine medizinische oder rechtliche Beratung dar und sollten nicht als solche verstanden werden.</p><p>Die Nutzung dieser App und ihrer Inhalte erfolgt auf eigene Verantwortung. Wir haften nicht für Schäden, die durch die Nutzung oder Nichtnutzung der bereitgestellten Informationen entstehen.</p>`;

interface LegalSettings {
  id: string;
  company_name: string | null;
  company_type: string;
  owner_name: string | null;
  street: string | null;
  zip_code: string | null;
  city: string | null;
  country: string;
  email: string | null;
  phone: string | null;
  register_court: string | null;
  register_number: string | null;
  vat_id: string | null;
  responsible_person: string | null;
  data_protection_officer: string | null;
  data_protection_officer_email: string | null;
  privacy_content: string | null;
  terms_content: string | null;
  content_updated_at: string | null;
  privacy_version: string;
  terms_version: string;
  health_consent_version: string;
  health_consent_content: string | null;
}

const COMPANY_TYPES = [
  { value: 'individual', label: 'Einzelunternehmer / Privatperson' },
  { value: 'gbr', label: 'GbR (Gesellschaft bürgerlichen Rechts)' },
  { value: 'ug', label: 'UG (haftungsbeschränkt)' },
  { value: 'gmbh', label: 'GmbH' },
  { value: 'ag', label: 'AG' },
  { value: 'ev', label: 'e.V. (eingetragener Verein)' },
  { value: 'other', label: 'Sonstige' },
];

const REQUIRED_FIELDS_BY_TYPE: Record<string, string[]> = {
  individual: ['owner_name', 'street', 'zip_code', 'city', 'email'],
  gbr: ['company_name', 'owner_name', 'street', 'zip_code', 'city', 'email'],
  ug: ['company_name', 'owner_name', 'street', 'zip_code', 'city', 'email', 'register_court', 'register_number'],
  gmbh: ['company_name', 'owner_name', 'street', 'zip_code', 'city', 'email', 'register_court', 'register_number'],
  ag: ['company_name', 'owner_name', 'street', 'zip_code', 'city', 'email', 'register_court', 'register_number'],
  ev: ['company_name', 'owner_name', 'street', 'zip_code', 'city', 'email', 'register_court', 'register_number'],
  other: ['company_name', 'owner_name', 'street', 'zip_code', 'city', 'email'],
};

export function LegalSettingsPage() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<LegalSettings>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Google Analytics state
  const [gaEnabled, setGaEnabled] = useState(false);
  const [gaId, setGaId] = useState('');

  // Privacy & Terms content state
  const [privacyContent, setPrivacyContent] = useState('');
  const [termsContent, setTermsContent] = useState('');
  const [disclaimerContent, setDisclaimerContent] = useState('');
  const [activeContentTab, setActiveContentTab] = useState<'privacy' | 'terms' | 'disclaimer' | 'health' | null>(null);

  // Versionen der Rechtstexte (Migration 172). Sie stehen bewusst neben dem
  // jeweiligen Text: record_consent() prueft gegen diese Werte, und nur ein
  // Wechsel hier fuehrt dazu, dass Bestandsnutzer erneut gefragt werden.
  // content_updated_at taugt dafuer nicht - der Speichern-Button setzt es
  // auch bei einem Tippfehler-Fix.
  const [privacyVersion, setPrivacyVersion] = useState('1.0');
  const [termsVersion, setTermsVersion] = useState('1.0');
  const [healthConsentVersion, setHealthConsentVersion] = useState('1.0');
  const [healthConsentContent, setHealthConsentContent] = useState('');

  // Fetch legal settings
  const { data: legalSettings, isLoading: loadingLegal } = useQuery({
    queryKey: ['legal-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('legal_settings')
        .select('*')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as LegalSettings | null;
    },
  });

  // Fetch GA settings
  const { data: gaSettings, isLoading: loadingGa } = useQuery({
    queryKey: ['ga-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', ['google_analytics_enabled', 'google_analytics_id']);

      if (error) throw error;
      return data as { key: string; value: unknown }[];
    },
  });

  // Fetch Disclaimer settings
  const { data: disclaimerSettings, isLoading: loadingDisclaimer } = useQuery({
    queryKey: ['disclaimer-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'disclaimer_content')
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data as any)?.value as string | null;
    },
  });

  // Initialize form data
  useEffect(() => {
    if (legalSettings) {
      setFormData(legalSettings);
      setPrivacyContent(legalSettings.privacy_content || '');
      setTermsContent(legalSettings.terms_content || '');
      setPrivacyVersion(legalSettings.privacy_version || '1.0');
      setTermsVersion(legalSettings.terms_version || '1.0');
      setHealthConsentVersion(legalSettings.health_consent_version || '1.0');
      setHealthConsentContent(legalSettings.health_consent_content || '');
    }
  }, [legalSettings]);

  useEffect(() => {
    if (gaSettings) {
      const enabled = gaSettings.find(s => s.key === 'google_analytics_enabled');
      const id = gaSettings.find(s => s.key === 'google_analytics_id');
      setGaEnabled(enabled?.value === true || enabled?.value === 'true');
      setGaId(typeof id?.value === 'string' ? id.value.replace(/"/g, '') : '');
    }
  }, [gaSettings]);

  useEffect(() => {
    if (disclaimerSettings !== undefined) {
      setDisclaimerContent(disclaimerSettings || DEFAULT_DISCLAIMER);
    }
  }, [disclaimerSettings]);

  // Save legal settings mutation
  const saveLegalMutation = useMutation({
    mutationFn: async (data: Partial<LegalSettings>) => {
      if (!legalSettings?.id) throw new Error('No legal settings found');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase
        .from('legal_settings') as any)
        .update(data)
        .eq('id', legalSettings.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-settings'] });
      toast.success('Rechtliche Angaben gespeichert');
    },
    onError: () => {
      toast.error('Fehler beim Speichern');
    },
  });

  // Save GA settings mutation
  const saveGaMutation = useMutation({
    mutationFn: async ({ enabled, id }: { enabled: boolean; id: string }) => {
      const updates = [
        { key: 'google_analytics_enabled', value: enabled },
        { key: 'google_analytics_id', value: id },
      ];

      for (const update of updates) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase
          .from('system_settings') as any)
          .update({ value: update.value, updated_at: new Date().toISOString() })
          .eq('key', update.key);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ga-settings'] });
      toast.success('Google Analytics Einstellungen gespeichert');
    },
    onError: () => {
      toast.error('Fehler beim Speichern');
    },
  });

  // Save Disclaimer mutation
  const saveDisclaimerMutation = useMutation({
    mutationFn: async (content: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase
        .from('system_settings') as any)
        .upsert({
          key: 'disclaimer_content',
          value: content,
          description: 'HTML content for the disclaimer shown on various pages (editable via Admin Panel)',
          updated_at: new Date().toISOString(),
        }, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['disclaimer-settings'] });
      toast.success('Haftungsausschluss gespeichert');
    },
    onError: () => {
      toast.error('Fehler beim Speichern');
    },
  });

  const handleInputChange = (field: keyof LegalSettings, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value || null }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // Include content fields in the save
    const dataToSave = {
      ...formData,
      privacy_content: privacyContent || null,
      terms_content: termsContent || null,
      privacy_version: privacyVersion.trim() || '1.0',
      terms_version: termsVersion.trim() || '1.0',
      health_consent_version: healthConsentVersion.trim() || '1.0',
      health_consent_content: healthConsentContent || null,
      content_updated_at: new Date().toISOString(),
    };
    saveLegalMutation.mutate(dataToSave);
    saveGaMutation.mutate({ enabled: gaEnabled, id: gaId });
    saveDisclaimerMutation.mutate(disclaimerContent);
    setHasChanges(false);
  };

  const requiredFields = REQUIRED_FIELDS_BY_TYPE[formData.company_type || 'individual'] || [];

  const isFieldRequired = (field: string) => requiredFields.includes(field);

  const getMissingFields = () => {
    return requiredFields.filter(field => !formData[field as keyof LegalSettings]);
  };

  const missingFields = getMissingFields();

  // Generate Impressum preview
  const generateImpressum = () => {
    const lines: string[] = [];

    if (formData.company_name) {
      lines.push(formData.company_name);
    }
    if (formData.owner_name) {
      const prefix = formData.company_type === 'individual' ? '' :
        ['gmbh', 'ug', 'ag'].includes(formData.company_type || '') ? 'Geschäftsführer: ' : 'Inhaber: ';
      lines.push(prefix + formData.owner_name);
    }
    if (formData.street) lines.push(formData.street);
    if (formData.zip_code || formData.city) {
      lines.push(`${formData.zip_code || ''} ${formData.city || ''}`.trim());
    }
    if (formData.country && formData.country !== 'Deutschland') {
      lines.push(formData.country);
    }

    lines.push(''); // Empty line

    if (formData.email) lines.push(`E-Mail: ${formData.email}`);
    if (formData.phone) lines.push(`Telefon: ${formData.phone}`);

    if (formData.register_court || formData.register_number) {
      lines.push('');
      if (formData.register_court) lines.push(`Registergericht: ${formData.register_court}`);
      if (formData.register_number) lines.push(`Registernummer: ${formData.register_number}`);
    }

    if (formData.vat_id) {
      lines.push('');
      lines.push(`USt-IdNr.: ${formData.vat_id}`);
    }

    if (formData.responsible_person) {
      lines.push('');
      lines.push(`Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV:`);
      lines.push(formData.responsible_person);
    }

    return lines.join('\n');
  };

  const isLoading = loadingLegal || loadingGa || loadingDisclaimer;

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Rechtliche Angaben</h1>
            <p className="text-slate-600">Impressum und Datenschutz-Einstellungen (TMG-Pflichtangaben)</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="px-4 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg font-medium flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Vorschau
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saveLegalMutation.isPending}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              Speichern
            </button>
          </div>
        </div>
      </div>

      {/* Missing fields warning */}
      {missingFields.length > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-amber-800">Unvollständige Angaben</h3>
            <p className="text-sm text-amber-700 mt-1">
              Für die gewählte Rechtsform fehlen Pflichtangaben. Das Impressum ist möglicherweise nicht rechtskonform.
            </p>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="mb-6 bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Impressum-Vorschau</h3>
            <button
              onClick={() => setShowPreview(false)}
              className="text-slate-400 hover:text-slate-600"
            >
              &times;
            </button>
          </div>
          <div className="p-6">
            <pre className="whitespace-pre-wrap font-sans text-slate-700">
              {generateImpressum() || 'Keine Angaben vorhanden'}
            </pre>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Company Type */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Rechtsform</h2>
                <p className="text-sm text-slate-500">Bestimmt die Pflichtangaben im Impressum</p>
              </div>
            </div>
            <select
              value={formData.company_type || 'individual'}
              onChange={(e) => handleInputChange('company_type', e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
            >
              {COMPANY_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Company/Person Info */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Betreiber-Informationen</h2>
                <p className="text-sm text-slate-500">Name und verantwortliche Personen</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {formData.company_type !== 'individual' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Firmenname {isFieldRequired('company_name') && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="text"
                    value={formData.company_name || ''}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    placeholder="z.B. ParentPath GmbH"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  />
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {formData.company_type === 'individual' ? 'Vollständiger Name' : 'Geschäftsführer / Inhaber'} {isFieldRequired('owner_name') && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  value={formData.owner_name || ''}
                  onChange={(e) => handleInputChange('owner_name', e.target.value)}
                  placeholder="Max Mustermann"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Adresse</h2>
                <p className="text-sm text-slate-500">Ladungsfähige Anschrift</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Straße + Hausnummer {isFieldRequired('street') && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  value={formData.street || ''}
                  onChange={(e) => handleInputChange('street', e.target.value)}
                  placeholder="Musterstraße 123"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  PLZ {isFieldRequired('zip_code') && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  value={formData.zip_code || ''}
                  onChange={(e) => handleInputChange('zip_code', e.target.value)}
                  placeholder="12345"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Stadt {isFieldRequired('city') && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  value={formData.city || ''}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="Osnabrück"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Land</label>
                <input
                  type="text"
                  value={formData.country || 'Deutschland'}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Kontakt</h2>
                <p className="text-sm text-slate-500">Erreichbarkeit für Nutzer und Behörden</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  E-Mail {isFieldRequired('email') && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="kontakt@parentpath.de"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
                <p className="text-xs text-slate-500 mt-1">Wird in Impressum & Datenschutz verwendet</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Telefon <span className="text-slate-400">(empfohlen)</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+49 541 12345678"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Register (for companies) */}
          {['ug', 'gmbh', 'ag', 'ev'].includes(formData.company_type || '') && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900">Handelsregister</h2>
                  <p className="text-sm text-slate-500">Pflichtangaben für juristische Personen</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Registergericht {isFieldRequired('register_court') && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="text"
                    value={formData.register_court || ''}
                    onChange={(e) => handleInputChange('register_court', e.target.value)}
                    placeholder="Amtsgericht Osnabrück"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Registernummer {isFieldRequired('register_number') && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="text"
                    value={formData.register_number || ''}
                    onChange={(e) => handleInputChange('register_number', e.target.value)}
                    placeholder="HRB 12345"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    USt-IdNr. <span className="text-slate-400">(falls vorhanden)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.vat_id || ''}
                    onChange={(e) => handleInputChange('vat_id', e.target.value)}
                    placeholder="DE123456789"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Data Protection */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Datenschutz</h2>
                <p className="text-sm text-slate-500">Verantwortliche für Datenschutz (optional bei weniger als 20 MA)</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Verantwortlich nach § 55 RStV
                </label>
                <input
                  type="text"
                  value={formData.responsible_person || ''}
                  onChange={(e) => handleInputChange('responsible_person', e.target.value)}
                  placeholder="Max Mustermann"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Datenschutzbeauftragter
                </label>
                <input
                  type="text"
                  value={formData.data_protection_officer || ''}
                  onChange={(e) => handleInputChange('data_protection_officer', e.target.value)}
                  placeholder="(bei weniger als 20 MA nicht erforderlich)"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  E-Mail des Datenschutzbeauftragten
                </label>
                <input
                  type="email"
                  value={formData.data_protection_officer_email || ''}
                  onChange={(e) => handleInputChange('data_protection_officer_email', e.target.value)}
                  placeholder="datenschutz@parentpath.de"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Google Analytics */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Google Analytics</h2>
                <p className="text-sm text-slate-500">Website-Tracking (aktiviert automatisch Cookie-Banner)</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Google Analytics aktivieren</p>
                  <p className="text-sm text-slate-500">Aktiviert Tracking und zeigt Cookie-Banner an</p>
                </div>
                <button
                  onClick={() => {
                    setGaEnabled(!gaEnabled);
                    setHasChanges(true);
                  }}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    gaEnabled ? 'bg-teal-500' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      gaEnabled ? 'translate-x-6' : ''
                    }`}
                  />
                </button>
              </div>

              {gaEnabled && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Measurement ID
                  </label>
                  <input
                    type="text"
                    value={gaId}
                    onChange={(e) => {
                      setGaId(e.target.value);
                      setHasChanges(true);
                    }}
                    placeholder="G-XXXXXXXXXX"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Finde deine ID unter Google Analytics → Verwaltung → Datenstreams
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Privacy Policy Content */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Lock className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900">Datenschutzerklärung</h2>
                  <p className="text-sm text-slate-500">Vollständig editierbarer Inhalt mit Platzhaltern</p>
                </div>
              </div>
              <button
                onClick={() => setActiveContentTab(activeContentTab === 'privacy' ? null : 'privacy')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeContentTab === 'privacy'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {activeContentTab === 'privacy' ? 'Schließen' : 'Bearbeiten'}
              </button>
            </div>

            {activeContentTab === 'privacy' && (
              <div className="space-y-4">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>Verfügbare Platzhalter:</strong>{' '}
                    <code className="bg-amber-100 px-1 rounded">{'{{company_name}}'}</code>,{' '}
                    <code className="bg-amber-100 px-1 rounded">{'{{address}}'}</code>,{' '}
                    <code className="bg-amber-100 px-1 rounded">{'{{email}}'}</code>,{' '}
                    <code className="bg-amber-100 px-1 rounded">{'{{data_protection_officer}}'}</code>,{' '}
                    <code className="bg-amber-100 px-1 rounded">{'{{dpo_email}}'}</code>
                  </p>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Version dieses Textes
                  </label>
                  <input
                    type="text"
                    value={privacyVersion}
                    onChange={(e) => {
                      setPrivacyVersion(e.target.value);
                      setHasChanges(true);
                    }}
                    className="w-32 px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="1.0"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Nur erhoehen, wenn sich der Text inhaltlich aendert. Jede Erhoehung
                    fuehrt dazu, dass alle Nutzer erneut zustimmen muessen.
                  </p>
                </div>
                <RichTextEditor
                  value={privacyContent}
                  onChange={(html) => {
                    setPrivacyContent(html);
                    setHasChanges(true);
                  }}
                  label=""
                  minHeight="400px"
                />
              </div>
            )}

            {activeContentTab !== 'privacy' && privacyContent && (
              <div className="text-sm text-slate-500">
                Letztes Update: {legalSettings?.content_updated_at
                  ? new Date(legalSettings.content_updated_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : 'Nie'}
              </div>
            )}
          </div>

          {/* Terms of Service Content */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <ScrollText className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900">Nutzungsbedingungen</h2>
                  <p className="text-sm text-slate-500">Vollständig editierbarer Inhalt mit Platzhaltern</p>
                </div>
              </div>
              <button
                onClick={() => setActiveContentTab(activeContentTab === 'terms' ? null : 'terms')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeContentTab === 'terms'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {activeContentTab === 'terms' ? 'Schließen' : 'Bearbeiten'}
              </button>
            </div>

            {activeContentTab === 'terms' && (
              <div className="space-y-4">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>Verfügbare Platzhalter:</strong>{' '}
                    <code className="bg-amber-100 px-1 rounded">{'{{company_name}}'}</code>,{' '}
                    <code className="bg-amber-100 px-1 rounded">{'{{address}}'}</code>,{' '}
                    <code className="bg-amber-100 px-1 rounded">{'{{email}}'}</code>
                  </p>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Version dieses Textes
                  </label>
                  <input
                    type="text"
                    value={termsVersion}
                    onChange={(e) => {
                      setTermsVersion(e.target.value);
                      setHasChanges(true);
                    }}
                    className="w-32 px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="1.0"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Nur erhoehen, wenn sich der Text inhaltlich aendert. Jede Erhoehung
                    fuehrt dazu, dass alle Nutzer erneut zustimmen muessen.
                  </p>
                </div>
                <RichTextEditor
                  value={termsContent}
                  onChange={(html) => {
                    setTermsContent(html);
                    setHasChanges(true);
                  }}
                  label=""
                  minHeight="400px"
                />
              </div>
            )}

            {activeContentTab !== 'terms' && termsContent && (
              <div className="text-sm text-slate-500">
                Letztes Update: {legalSettings?.content_updated_at
                  ? new Date(legalSettings.content_updated_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : 'Nie'}
              </div>
            )}
          </div>

          {/* Art.-9-Einwilligung (Gesundheitsdaten) */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900">Einwilligung Gesundheitsdaten</h2>
                  <p className="text-sm text-slate-500">
                    Art. 9 DSGVO - wird im Consent-Gate und bei der Registrierung angezeigt
                  </p>
                </div>
              </div>
              <button
                onClick={() => setActiveContentTab(activeContentTab === 'health' ? null : 'health')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeContentTab === 'health'
                    ? 'bg-rose-100 text-rose-700'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {activeContentTab === 'health' ? 'Schließen' : 'Bearbeiten'}
              </button>
            </div>

            {activeContentTab === 'health' && (
              <div className="space-y-4">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>Verfügbare Platzhalter:</strong>{' '}
                    <code className="bg-amber-100 px-1 rounded">{'{{company_name}}'}</code>,{' '}
                    <code className="bg-amber-100 px-1 rounded">{'{{email}}'}</code>,{' '}
                    <code className="bg-amber-100 px-1 rounded">{'{{dpo_email}}'}</code>
                  </p>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Version dieses Textes
                  </label>
                  <input
                    type="text"
                    value={healthConsentVersion}
                    onChange={(e) => {
                      setHealthConsentVersion(e.target.value);
                      setHasChanges(true);
                    }}
                    className="w-32 px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="1.0"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Nur erhoehen, wenn sich der Text inhaltlich aendert. Jede Erhoehung
                    fuehrt dazu, dass alle Nutzer erneut einwilligen muessen - bis dahin
                    bleibt die App fuer sie gesperrt.
                  </p>
                </div>
                <RichTextEditor
                  value={healthConsentContent}
                  onChange={(html) => {
                    setHealthConsentContent(html);
                    setHasChanges(true);
                  }}
                  label=""
                  minHeight="300px"
                />
              </div>
            )}

            {activeContentTab !== 'health' && (
              <div className="text-sm text-slate-500">
                Aktuelle Version: {legalSettings?.health_consent_version || '1.0'}
              </div>
            )}
          </div>

          {/* Disclaimer Content */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900">Haftungsausschluss</h2>
                  <p className="text-sm text-slate-500">Wird unter Seiteninhalten angezeigt (z.B. SSW, Checklisten)</p>
                </div>
              </div>
              <button
                onClick={() => setActiveContentTab(activeContentTab === 'disclaimer' ? null : 'disclaimer')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeContentTab === 'disclaimer'
                    ? 'bg-amber-100 text-amber-700'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {activeContentTab === 'disclaimer' ? 'Schließen' : 'Bearbeiten'}
              </button>
            </div>

            {activeContentTab === 'disclaimer' && (
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Hinweis:</strong> Der Haftungsausschluss wird unter dem Inhalt verschiedener Seiten angezeigt.
                    Er sollte allgemein gehalten sein und darauf hinweisen, dass die App keine medizinische Beratung ersetzt.
                  </p>
                </div>
                <RichTextEditor
                  value={disclaimerContent}
                  onChange={(html) => {
                    setDisclaimerContent(html);
                    setHasChanges(true);
                  }}
                  label=""
                  minHeight="300px"
                />
              </div>
            )}

            {activeContentTab !== 'disclaimer' && disclaimerContent && (
              <div className="text-sm text-slate-500">
                Inhalt konfiguriert ({disclaimerContent.length} Zeichen)
              </div>
            )}
          </div>

          {/* Info */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-800">Rechtlicher Hinweis</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Diese Angaben werden automatisch im Impressum und in der Datenschutzerklärung der App angezeigt.
                  Stelle sicher, dass alle Pflichtangaben korrekt und aktuell sind, um rechtliche Probleme zu vermeiden.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
